<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';
import SparklineChart from './SparklineChart.vue';

const props = defineProps({
  icon: {
    type: String,
    default: 'i-lucide-activity',
  },
  title: {
    type: String,
    required: true,
  },
  value: {
    type: [String, Number],
    required: true,
  },
  subtitle: {
    type: String,
    default: '',
  },
  change: {
    type: [String, Number],
    default: null,
  },
  color: {
    type: String,
    default: 'blue',
    validator: value =>
      [
        'blue',
        'green',
        'amber',
        'red',
        'purple',
        'slate',
        'teal',
        'violet',
      ].includes(value),
  },
  sparklineData: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const iconColorClass = computed(() => {
  const colorMap = {
    blue: 'bg-n-blue-3 text-n-blue-11',
    green: 'bg-n-teal-3 text-n-teal-11',
    amber: 'bg-n-amber-3 text-n-amber-11',
    red: 'bg-n-ruby-3 text-n-ruby-11',
    purple: 'bg-n-violet-3 text-n-violet-11',
    violet: 'bg-n-violet-3 text-n-violet-11',
    slate: 'bg-n-alpha-2 text-n-slate-11',
    teal: 'bg-n-teal-3 text-n-teal-11',
  };
  return colorMap[props.color] || colorMap.blue;
});

const changeIsPositive = computed(() => {
  if (props.change === null) return null;
  return Number(props.change) >= 0;
});

const changeClass = computed(() => {
  if (changeIsPositive.value === null) return '';
  return changeIsPositive.value ? 'text-n-teal-11' : 'text-n-ruby-11';
});

const sparklineColor = computed(() => {
  const colorMap = {
    blue: '#3b82f6',
    green: '#14b8a6',
    amber: '#f59e0b',
    red: '#ef4444',
    purple: '#8b5cf6',
    violet: '#8b5cf6',
    slate: '#64748b',
    teal: '#14b8a6',
  };
  return colorMap[props.color] || colorMap.blue;
});
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <!-- Header -->
    <div class="flex items-center justify-between mb-3">
      <div class="flex items-center gap-2">
        <div
          class="w-8 h-8 rounded-lg flex items-center justify-center"
          :class="iconColorClass"
        >
          <span :class="icon" class="text-lg" />
        </div>
        <span class="text-sm font-medium text-n-slate-11">{{ title }}</span>
      </div>
      <div
        v-if="change !== null"
        class="flex items-center gap-1 text-xs font-medium"
        :class="changeClass"
      >
        <span
          :class="
            changeIsPositive ? 'i-lucide-trending-up' : 'i-lucide-trending-down'
          "
        />
        {{ changeIsPositive ? '+' : '' }}{{ change }}%
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-4">
      <Spinner size="small" />
    </div>

    <!-- Content -->
    <div v-else class="flex-1">
      <!-- Value -->
      <div class="text-3xl font-bold text-n-slate-12 mb-1">
        {{ value }}
      </div>

      <!-- Subtitle -->
      <div v-if="subtitle" class="text-sm text-n-slate-11">
        {{ subtitle }}
      </div>

      <!-- Sparkline -->
      <div v-if="sparklineData && sparklineData.length > 0" class="mt-3 h-10">
        <SparklineChart :data="sparklineData" :color="sparklineColor" />
      </div>
    </div>
  </div>
</template>
